import { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Tabs,
  Tab,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Checkbox,
  Typography,
  InputAdornment,
  CircularProgress,
  Chip,
} from '@mui/material'
import { Search, Person, Groups } from '@mui/icons-material'
import { useMutation, useQuery } from '@tanstack/react-query'
import { chatApi } from '@api/endpoints/chat'
import { ChatUserSearchResult } from '@api/types/chat'
import { useAuthStore } from '@store/authStore'

interface NewChatDialogProps {
  open: boolean
  onClose: () => void
  onChatCreated: (conversationId: string) => void
}

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      sx={{ pt: 2 }}
    >
      {value === index && children}
    </Box>
  )
}

export function NewChatDialog({ open, onClose, onChatCreated }: NewChatDialogProps) {
  const [tabIndex, setTabIndex] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedUsers, setSelectedUsers] = useState<ChatUserSearchResult[]>([])
  const [groupName, setGroupName] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [searchResults, setSearchResults] = useState<ChatUserSearchResult[]>([])
  const { user } = useAuthStore()

  // Search users with debounce
  const handleSearch = async (query: string) => {
    setSearchQuery(query)
    if (!query.trim()) {
      setSearchResults([])
      return
    }

    setIsSearching(true)
    try {
      const results = await chatApi.searchUsers(query)
      // Filter out the current user from search results
      const filteredResults = results.filter((u) => u.id !== user?.userId)
      setSearchResults(filteredResults)
    } catch (error) {
      console.error('Error searching users:', error)
      setSearchResults([])
    } finally {
      setIsSearching(false)
    }
  }

  // Create direct conversation
  const createDirectMutation = useMutation({
    mutationFn: chatApi.createDirectConversation,
    onSuccess: (conversation) => {
      onChatCreated(conversation.id)
      handleClose()
    },
  })

  // Create group conversation
  const createGroupMutation = useMutation({
    mutationFn: chatApi.createGroupConversation,
    onSuccess: (conversation) => {
      onChatCreated(conversation.id)
      handleClose()
    },
  })

  const handleClose = () => {
    setTabIndex(0)
    setSearchQuery('')
    setSelectedUsers([])
    setGroupName('')
    setSearchResults([])
    onClose()
  }

  const MAX_GROUP_PARTICIPANTS = 10

  const handleUserSelect = (user: ChatUserSearchResult) => {
    if (tabIndex === 0) {
      // Direct message - create immediately
      createDirectMutation.mutate({ recipientId: user.id })
    } else {
      // Group - toggle selection
      setSelectedUsers((prev) => {
        const isSelected = prev.some((u) => u.id === user.id)
        if (isSelected) {
          return prev.filter((u) => u.id !== user.id)
        }
        if (prev.length >= MAX_GROUP_PARTICIPANTS) return prev
        setSearchQuery('')
        setSearchResults([])
        return [...prev, user]
      })
    }
  }

  const handleRemoveSelected = (userId: string) => {
    setSelectedUsers((prev) => prev.filter((u) => u.id !== userId))
  }

  const handleCreateGroup = () => {
    if (!groupName.trim() || selectedUsers.length === 0) return
    createGroupMutation.mutate({
      name: groupName.trim(),
      participantIds: selectedUsers.map((u) => u.id),
    })
  }

  const isLoading = createDirectMutation.isPending || createGroupMutation.isPending

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>New Conversation</DialogTitle>
      <DialogContent>
        <Tabs
          value={tabIndex}
          onChange={(_, newValue) => setTabIndex(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<Person />} label="Direct Message" iconPosition="start" />
          <Tab icon={<Groups />} label="Group Chat" iconPosition="start" />
        </Tabs>

        {/* Direct Message Tab */}
        <TabPanel value={tabIndex} index={0}>
          <TextField
            fullWidth
            autoFocus
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: isSearching && (
                <InputAdornment position="end">
                  <CircularProgress size={20} />
                </InputAdornment>
              ),
            }}
          />
          <List sx={{ maxHeight: 300, overflow: 'auto', mt: 1 }}>
            {searchResults.length === 0 && searchQuery && !isSearching && (
              <Typography color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                No users found
              </Typography>
            )}
            {searchResults.map((user) => (
              <ListItemButton
                key={user.id}
                onClick={() => handleUserSelect(user)}
                disabled={isLoading}
              >
                <ListItemAvatar>
                  <Avatar src={user.avatarUrl || undefined}>
                    {user.displayName?.charAt(0).toUpperCase() || user.userName?.charAt(0).toUpperCase()}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={user.displayName || user.userName}
                  secondary={user.userName}
                />
                {user.isOnline && (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: 'success.main',
                    }}
                  />
                )}
              </ListItemButton>
            ))}
          </List>
        </TabPanel>

        {/* Group Chat Tab */}
        <TabPanel value={tabIndex} index={1}>
          <TextField
            fullWidth
            label="Group Name"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            sx={{ mb: 2 }}
          />

          {/* Selected users */}
          {selectedUsers.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color={selectedUsers.length >= MAX_GROUP_PARTICIPANTS ? 'error' : 'text.secondary'} sx={{ mb: 0.5, display: 'block' }}>
                {selectedUsers.length} / {MAX_GROUP_PARTICIPANTS} participants
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selectedUsers.map((user) => (
                  <Chip
                    key={user.id}
                    avatar={<Avatar src={user.avatarUrl || undefined}>{user.userName?.charAt(0)}</Avatar>}
                    label={user.displayName || user.userName}
                    onDelete={() => handleRemoveSelected(user.id)}
                    size="small"
                  />
                ))}
              </Box>
            </Box>
          )}

          <TextField
            fullWidth
            placeholder="Search users to add..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: isSearching && (
                <InputAdornment position="end">
                  <CircularProgress size={20} />
                </InputAdornment>
              ),
            }}
          />
          <List sx={{ maxHeight: 200, overflow: 'auto', mt: 1 }}>
            {searchResults
              .filter((user) => !selectedUsers.some((u) => u.id === user.id))
              .map((user) => (
                <ListItemButton
                  key={user.id}
                  onClick={() => handleUserSelect(user)}
                  disabled={selectedUsers.length >= MAX_GROUP_PARTICIPANTS}
                >
                  <Checkbox
                    edge="start"
                    checked={false}
                    disabled={selectedUsers.length >= MAX_GROUP_PARTICIPANTS}
                  />
                  <ListItemAvatar>
                    <Avatar src={user.avatarUrl || undefined}>
                      {user.displayName?.charAt(0).toUpperCase() || user.userName?.charAt(0).toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={user.displayName || user.userName}
                    secondary={user.userName}
                  />
                </ListItemButton>
              ))}
          </List>
        </TabPanel>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        {tabIndex === 1 && (
          <Button
            variant="contained"
            onClick={handleCreateGroup}
            disabled={!groupName.trim() || selectedUsers.length === 0 || isLoading}
          >
            {isLoading ? <CircularProgress size={20} /> : 'Create Group'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
